import { useCallback } from 'react';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';
import { hasPermission } from '../utils/permissionControl';
import {
  displayPickPointOnMap as displayPickPointOnMapUtil,
  removePickPointFromMap as removePickPointFromMapUtil,
  fetchPickPointsForMap,
  deletePickPointById
} from '../utils/dashboard';

/**
 * 指挥系统：手机选点（地图显示/删除/加载/提交弹窗/飞行/确认上报）
 * 自 pages/DashboardAuthenticated.js 原样迁出；入参均为稳定引用（useRef / setState）。
 */
export default function useDashboardPickPointActions({
  mapRef,
  pickPointMarkersRef,
  currentBasemapIdRef,
  setPickPoints,
  setPickPointSubmittedModal
}) {
  const handleDeletePickPoint = useCallback(async (point) => {
    if (!point?.id) return;
    if (!hasPermission('手机选点删除')) {
      message.error('无「手机选点删除」权限');
      return;
    }
    if (!window.confirm('确定删除该手机选点？删除后地图上将不再显示。')) return;
    try {
      await deletePickPointById(apiFetch, point.id);
      removePickPointFromMapUtil(pickPointMarkersRef.current, point.id);
      setPickPoints((prev) => prev.filter((p) => String(p.id) !== String(point.id)));
      message.success('手机选点已删除');
    } catch (error) {
      message.error(error.message || '删除选点失败');
    }
  }, [pickPointMarkersRef, setPickPoints]);

  const displayPickPointOnMap = useCallback((point, basemapId) => {
    displayPickPointOnMapUtil(
      mapRef.current,
      point,
      pickPointMarkersRef.current,
      basemapId || currentBasemapIdRef.current,
      {
        canDelete: hasPermission('手机选点删除'),
        onDelete: handleDeletePickPoint
      }
    );
  }, [mapRef, pickPointMarkersRef, currentBasemapIdRef, handleDeletePickPoint]);

  const removePickPointFromMap = useCallback((id) => {
    removePickPointFromMapUtil(pickPointMarkersRef.current, id);
  }, [pickPointMarkersRef]);

  const loadPickPoints = useCallback(async () => {
    try {
      const tokens = await fetchPickPointsForMap(apiFetch);
      setPickPoints(tokens);
      const basemapId = currentBasemapIdRef.current;
      tokens.forEach((point) => displayPickPointOnMap(point, basemapId));
    } catch (error) {
      console.warn('加载手机选点失败:', error.message);
    }
  }, [currentBasemapIdRef, setPickPoints, displayPickPointOnMap]);

  // 处理选点提交事件：显示弹窗
  const handlePickPointSubmitted = useCallback((point) => {
    console.log('收到选点提交:', point);
    setPickPointSubmittedModal({
      visible: true,
      point,
      confirming: false
    });
  }, [setPickPointSubmittedModal]);

  // 飞行到选点位置
  const flyToPickPoint = useCallback((point) => {
    const map = mapRef.current;
    if (!map) return;

    const lat = point.auto_lat || point.used_lat;
    const lng = point.auto_lng || point.used_lng;

    if (!lat || !lng) {
      message.warning('该选点暂无有效坐标');
      return;
    }

    try {
      map.flyTo({
        center: [lng, lat],
        zoom: 16,
        essential: true,
        duration: 2000
      });
      message.success('正在飞行到选点位置');
    } catch (error) {
      console.error('飞行失败:', error);
      message.error('飞行到选点位置失败');
    }
  }, [mapRef]);

  // 确认选点上报
  const confirmPickPoint = useCallback(async (point) => {
    setPickPointSubmittedModal((prev) => ({ ...prev, confirming: true }));

    try {
      const response = await apiFetch('/api/pick-point/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: point.token })
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || '确认失败');
      }

      message.success('已确认位置上报');
      setPickPointSubmittedModal({ visible: false, point: null, confirming: false });

      // 更新选点列表
      setPickPoints((prev) =>
        prev.map((p) => (p.id === point.id ? { ...p, ...data.point } : p))
      );
    } catch (error) {
      console.error('确认选点失败:', error);
      message.error(error.message || '确认选点失败');
      setPickPointSubmittedModal((prev) => ({ ...prev, confirming: false }));
    }
  }, [setPickPoints, setPickPointSubmittedModal]);

  return {
    handleDeletePickPoint,
    displayPickPointOnMap,
    removePickPointFromMap,
    loadPickPoints,
    handlePickPointSubmitted,
    flyToPickPoint,
    confirmPickPoint
  };
}
